import { useState, useEffect } from "react";
import { settingsApi } from "@/integrations/mysql/client";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PhotoCropUpload } from "@/components/ui/PhotoCropUpload";
import { useToast } from "@/hooks/use-toast";
import { Save, Plus, Trash2, GripVertical, Loader2, ArrowRight, ExternalLink, Globe, Calendar } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DEFAULT_LINKS, SOCIAL_TYPES, isSocialType, NAV_ICONS, getNavIcon } from "./MobileLinkTree";
import type { MobileLink, SocialTypeId } from "./MobileLinkTree";

const BUTTON_TYPES: { value: "primary" | "default"; label: string; color: string; Icon: React.ElementType }[] = [
  { value: "primary", label: "Primário", color: "bg-brand-primary/10 text-brand-primary border-brand-primary/20", Icon: Calendar },
  { value: "default", label: "Padrão",   color: "bg-gray-100 text-gray-600 border-gray-200",                      Icon: Globe },
];

function newLink(): MobileLink {
  return { id: Date.now().toString(), title: "", description: "", url: "", type: "default", is_active: true };
}

// ─── Sortable link row (buttons) ─────────────────────────────────────────────
function SortableLink({ link, onChange, onRemove }: {
  link: MobileLink;
  onChange: (l: MobileLink) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: link.id });
  const typeInfo = BUTTON_TYPES.find(t => t.value === link.type) ?? BUTTON_TYPES[1];

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={[
        "rounded-2xl border transition-all",
        isDragging ? "shadow-lg scale-[1.01] bg-white border-brand-primary/30" : "bg-white border-gray-100 shadow-sm",
        !link.is_active ? "opacity-50" : "",
      ].join(" ")}
    >
      {/* Top row */}
      <div className="flex items-center gap-2 px-3 py-3">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0">
          <GripVertical className="w-4 h-4" />
        </div>
        <div className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-xs font-semibold shrink-0 ${typeInfo.color}`}>
          <typeInfo.Icon className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">{typeInfo.label}</span>
        </div>
        <Input
          placeholder="Título do link"
          value={link.title}
          onChange={e => onChange({ ...link, title: e.target.value })}
          className="text-sm font-medium flex-1 min-w-0 border-gray-200 focus:border-brand-primary"
        />
        <Switch checked={link.is_active} onCheckedChange={v => onChange({ ...link, is_active: v })} />
        <button onClick={onRemove} className="shrink-0 w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Detail rows */}
      <div className="border-t border-gray-50 px-3 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">URL</label>
            <Input placeholder="/blog ou https://..." value={link.url} onChange={e => onChange({ ...link, url: e.target.value })} className="text-sm border-gray-200 focus:border-brand-primary" />
          </div>
          <div>
            <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">Descrição</label>
            <Input placeholder="Subtítulo opcional" value={link.description ?? ""} onChange={e => onChange({ ...link, description: e.target.value })} className="text-sm border-gray-200 focus:border-brand-primary" />
          </div>
        </div>

        {/* Tipo: Primário / Padrão toggle */}
        <div>
          <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">Tipo</label>
          <div className="flex gap-2">
            {BUTTON_TYPES.map(t => {
              const selected = link.type === t.value;
              return (
                <button key={t.value} type="button"
                  onClick={() => onChange({ ...link, type: t.value })}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${selected ? t.color + ' border-current' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                  <t.Icon className="w-3.5 h-3.5" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Ícone: visual picker */}
        <div>
          <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1.5">Ícone</label>
          <div className="flex flex-wrap gap-1.5">
            {NAV_ICONS.map(n => {
              const selected = (link.icon ?? (link.type === "primary" ? "Calendar" : "Globe")) === n.id;
              return (
                <button key={n.id} type="button"
                  onClick={() => onChange({ ...link, icon: n.id })}
                  title={n.label}
                  className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg border transition-all text-[10px] font-medium ${
                    selected
                      ? 'border-brand-primary bg-brand-primary/8 text-brand-primary'
                      : 'border-gray-100 text-gray-400 hover:border-gray-300 hover:text-gray-600'
                  }`}>
                  <n.Icon className="w-4 h-4" />
                  <span>{n.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Social link row (footer icons) ──────────────────────────────────────────
function SocialLink({ link, onChange, onRemove }: {
  link: MobileLink;
  onChange: (l: MobileLink) => void;
  onRemove: () => void;
}) {
  const social = SOCIAL_TYPES.find(s => s.id === link.type) ?? SOCIAL_TYPES[0];

  return (
    <div className={`rounded-2xl border bg-white shadow-sm transition-all ${!link.is_active ? "opacity-50" : ""}`}>
      {/* Social icon picker */}
      <div className="px-3 pt-3 pb-2">
        <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-2">Rede Social</label>
        <div className="flex flex-wrap gap-2">
          {SOCIAL_TYPES.map(s => {
            const selected = link.type === s.id;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => onChange({ ...link, type: s.id, title: link.title || s.label })}
                title={s.label}
                className={`flex flex-col items-center gap-1 px-2.5 py-2 rounded-xl border transition-all text-[10px] font-semibold ${
                  selected
                    ? `${s.color} border-current`
                    : "border-gray-100 text-gray-400 hover:border-gray-300 hover:text-gray-600"
                }`}
              >
                <s.Icon className="w-4 h-4" />
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* URL + toggle + delete */}
      <div className="border-t border-gray-50 px-3 py-3 flex items-end gap-3">
        <div className="flex-1">
          <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide block mb-1">URL</label>
          <Input
            placeholder={social.defaultUrl}
            value={link.url}
            onChange={e => onChange({ ...link, url: e.target.value })}
            className="text-sm border-gray-200 focus:border-brand-primary"
          />
        </div>
        <Switch checked={link.is_active} onCheckedChange={v => onChange({ ...link, is_active: v })} />
        <button onClick={onRemove} className="w-8 h-9 flex items-center justify-center rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors shrink-0">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Phone preview ────────────────────────────────────────────────────────────
function PreviewLinkIcon({ link }: { link: MobileLink }) {
  const social = SOCIAL_TYPES.find(s => s.id === link.type);
  if (social) return <social.Icon size={15} className="text-white/80" />;
  const Icon = getNavIcon(link.icon ?? (link.type === "primary" ? "Calendar" : "Globe"));
  return <Icon className="w-4 h-4 text-white/80" />;
}

// Mirrors MobileLinkTree exactly, scaled to fit the phone mockup (260px wide ≈ 0.67× real phone)
function PhonePreview({ photo, heroImage, name, title, bio, links }: {
  photo: string; heroImage: string; name: string; title: string; bio: string; links: MobileLink[];
}) {
  const activeLinks = links.filter(l => l.is_active);
  const isExternal  = (url: string) => url.startsWith("http");

  // Same logic as MobileLinkTree:
  // background = heroImage always; profile circle = photo || heroImage
  const profileSrc = photo || heroImage;

  return (
    <div className="sticky top-6 flex flex-col items-center">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">Prévia</p>

      {/* Phone frame — 260px ≈ 0.67× iPhone 14 width */}
      <div
        className="relative w-[260px] rounded-[36px] overflow-hidden shadow-2xl border-[6px] border-gray-800 flex flex-col"
        style={{ background: "linear-gradient(180deg,#0e2d1c 0%,#1a4a2a 100%)" }}
      >
        {/* Status bar */}
        <div className="flex justify-between items-center px-5 pt-2.5 pb-1 shrink-0">
          <span className="text-white/50 text-[9px]">9:41</span>
          <div className="flex gap-1 items-center">
            <div className="w-3 h-1.5 rounded-sm bg-white/40" />
            <div className="w-1 h-1 rounded-full bg-white/40" />
          </div>
        </div>

        {/* Hero photo — MobileLinkTree uses h-64 on real phone; scaled ~0.67× = ~h-44 */}
        <div className="relative h-44 shrink-0 overflow-hidden">
          {heroImage
            ? <img src={heroImage} alt={name} className="w-full h-full object-cover object-top" />
            : <div className="w-full h-full bg-brand-primary/20" />
          }
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-[#0e2d1c]" />
        </div>

        {/* Profile — MobileLinkTree uses -mt-14, circle w-24 h-24; scaled: -mt-9, w-16 h-16 */}
        <div className="-mt-9 z-10 flex flex-col items-center px-4 text-center">
          <div className="w-16 h-16 rounded-full border-[3px] border-white/25 shadow-xl overflow-hidden bg-[#1F5938] mb-2 shrink-0">
            {profileSrc
              ? <img src={profileSrc} alt={name} className="w-full h-full object-cover object-top" />
              : <div className="w-full h-full bg-[#1F5938]" />
            }
          </div>
          <h1 className="text-white font-bold text-sm leading-tight">{name || "Seu nome"}</h1>
          <p className="text-white/55 text-[10px] mt-0.5 tracking-wide">{title || "Especialidade"}</p>
          {bio && (
            <p className="text-white/50 text-[9px] leading-relaxed mt-1 max-w-[200px] line-clamp-3">{bio}</p>
          )}
        </div>

        {/* Links — MobileLinkTree uses px-5 pt-5 pb-8 space-y-2; scaled: px-3 pt-3 pb-5 space-y-1.5 */}
        <div className="px-3 pt-3 pb-5 space-y-1.5 z-10">
          {activeLinks.filter(l => !isSocialType(l.type)).length === 0 ? (
            <p className="text-white/25 text-[10px] text-center py-3">Nenhum link ativo</p>
          ) : activeLinks.filter(l => !isSocialType(l.type)).map(link => {
            const isWa = link.type === "whatsapp";
            return (
              <div
                key={link.id}
                className={[
                  "flex items-center gap-2.5 w-full px-3 py-2.5 rounded-2xl border transition-all duration-150",
                  isWa ? "bg-[#1a5230] border-white/20" : "bg-white/[0.08] border-white/[0.12]",
                ].join(" ")}
              >
                {/* Icon box */}
                <div className={["w-8 h-8 rounded-xl flex items-center justify-center shrink-0", isWa ? "bg-white/20" : "bg-white/10"].join(" ")}>
                  <PreviewLinkIcon link={link} />
                </div>
                {/* Text */}
                <div className="flex-1 min-w-0 text-left">
                  <p className={["font-semibold text-[11px] leading-tight truncate", isWa ? "text-white" : "text-white/85"].join(" ")}>
                    {link.title || "Título"}
                  </p>
                  {link.description && (
                    <p className="text-white/45 text-[9px] mt-0.5 truncate">{link.description}</p>
                  )}
                </div>
                {/* Arrow */}
                {isExternal(link.url)
                  ? <ExternalLink className="w-3 h-3 text-white/30 shrink-0" />
                  : <ArrowRight   className="w-3 h-3 text-white/30 shrink-0" />
                }
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="pb-4 text-center shrink-0">
          <div className="flex items-center justify-center gap-2.5 mb-1.5">
            {activeLinks.filter(l => isSocialType(l.type)).map(l => {
              const s = SOCIAL_TYPES.find(s => s.id === l.type);
              return s ? <s.Icon key={l.id} size={12} className="text-white/30" /> : null;
            })}
          </div>
          <p className="text-[8px] uppercase tracking-[0.2em] text-white/20">
            <span style={{ color: "rgba(201,168,76,0.6)" }}>⚜</span> Clínica Equilíbrio
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export function MobileHomeManager() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [photo,     setPhoto]     = useState('');
  const [heroImage, setHeroImage] = useState('/camisa-preta.JPG');
  const [name,      setName]      = useState('');
  const [title,     setTitle]     = useState('');
  const [bio,       setBio]       = useState('');
  const [links,     setLinks]     = useState<MobileLink[]>([]);
  const [saving,    setSaving]    = useState(false);
  const [loaded,    setLoaded]    = useState(false);

  useEffect(() => {
    Promise.all([
      settingsApi.list(),
      fetch('/api/page-content?page=home').then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([rows, pageContent]) => {
      const get = (key: string) => rows.find((r: any) => r.key === key)?.value ?? '';
      setPhoto(get('mobile_home_photo'));
      setName(get('mobile_home_name')  || 'Dra. Brenda Ramos');
      setTitle(get('mobile_home_title') || 'Nutricionista CRN: 3/63492');
      setBio(get('mobile_home_bio')    || 'Especialista em nutrição clínica e emagrecimento saudável.');
      const rawLinks = get('mobile_home_links');
      if (rawLinks) {
        try {
          const parsed = JSON.parse(rawLinks);
          setLinks(Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_LINKS);
        } catch { setLinks(DEFAULT_LINKS); }
      } else {
        setLinks(DEFAULT_LINKS);
      }

      // Load hero image same way MobileLinkTree does
      const heroEntry = Array.isArray(pageContent)
        ? pageContent.find((e: any) => e.section === 'hero' && e.key === 'image')
        : null;
      const heroUrl = heroEntry?.content_value?.url ?? heroEntry?.value ?? '';
      if (heroUrl) setHeroImage(heroUrl);

      setLoaded(true);
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setLinks(prev => arrayMove(prev, prev.findIndex(l => l.id === active.id), prev.findIndex(l => l.id === over.id)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        settingsApi.update('mobile_home_photo',  photo),
        settingsApi.update('mobile_home_name',   name),
        settingsApi.update('mobile_home_title',  title),
        settingsApi.update('mobile_home_bio',    bio),
        settingsApi.update('mobile_home_links',  JSON.stringify(links)),
      ]);
      queryClient.invalidateQueries({ queryKey: ['site-settings'] });
      toast({ title: "Página mobile salva!" });
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <div className="p-4 text-sm text-gray-400">Carregando...</div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_280px] gap-6 items-start">

      {/* ── Editor ── */}
      <div className="space-y-5">
        {/* Profile */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Perfil</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-6 items-start">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600 block mb-2">Foto de Perfil</Label>
                <PhotoCropUpload
                  value={photo}
                  onChange={setPhoto}
                  shape="circle"
                  aspect={1}
                  label="Foto de perfil mobile"
                  previewClassName="w-16 h-16 rounded-full"
                />
                <p className="text-[10px] text-gray-400 mt-1.5">Se vazia, usa a foto do hero</p>
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Nome</Label>
                  <Input className="mt-1" value={name} onChange={e => setName(e.target.value)} placeholder="Dra. Brenda Ramos" />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Título / Especialidade</Label>
                  <Input className="mt-1" value={title} onChange={e => setTitle(e.target.value)} placeholder="Nutricionista CRN: 3/63492" />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-gray-600">Bio</Label>
              <Textarea className="mt-1" value={bio} onChange={e => setBio(e.target.value)} rows={2} maxLength={300} />
            </div>
          </CardContent>
        </Card>

        {/* Links — Botões */}
        <Card>
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm">Botões de navegação</CardTitle>
              <p className="text-xs text-gray-400 mt-0.5">Aparecem como botões grandes na tela mobile</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setLinks(p => [...p, newLink()])}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-gray-400 flex items-center gap-1.5 mb-1">
              <GripVertical className="w-3.5 h-3.5" />Arraste para reordenar
            </p>
            {links.filter(l => !isSocialType(l.type)).length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">Nenhum botão. Clique em Adicionar.</p>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={links.map(l => l.id)} strategy={verticalListSortingStrategy}>
                {links
                  .map((link, i) => ({ link, i }))
                  .filter(({ link }) => !isSocialType(link.type))
                  .map(({ link, i }) => (
                    <SortableLink
                      key={link.id}
                      link={link}
                      onChange={updated => setLinks(p => p.map((l, idx) => idx === i ? updated : l))}
                      onRemove={() => setLinks(p => p.filter((_, idx) => idx !== i))}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </CardContent>
        </Card>

        {/* Links — Rodapé social */}
        <Card className="border-pink-100">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                <span>Ícones do rodapé</span>
                <span className="text-[10px] font-normal bg-pink-50 text-pink-500 border border-pink-200 px-2 py-0.5 rounded-full">WhatsApp · Instagram · e mais</span>
              </CardTitle>
              <p className="text-xs text-gray-400 mt-0.5">Aparecem como ícones pequenos no rodapé da tela mobile</p>
            </div>
            <Button size="sm" variant="outline"
              onClick={() => setLinks(p => [...p, { id: Date.now().toString(), title: "WhatsApp", description: "", url: "https://wa.me/", type: "whatsapp", is_active: true }])}>
              <Plus className="w-3.5 h-3.5 mr-1" /> Adicionar
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {links.filter(l => isSocialType(l.type)).length === 0 && (
              <p className="text-sm text-gray-400 text-center py-4">Nenhuma rede social. Clique em Adicionar.</p>
            )}
            {links
              .map((link, i) => ({ link, i }))
              .filter(({ link }) => isSocialType(link.type))
              .map(({ link, i }) => (
                <SocialLink
                  key={link.id}
                  link={link}
                  onChange={updated => setLinks(p => p.map((l, idx) => idx === i ? updated : l))}
                  onRemove={() => setLinks(p => p.filter((_, idx) => idx !== i))}
                />
              ))}
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Salvar Página Mobile
          </Button>
        </div>
      </div>

      {/* ── Live preview ── */}
      <PhonePreview photo={photo} heroImage={heroImage} name={name} title={title} bio={bio} links={links} />
    </div>
  );
}
